import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationService } from '../../services/api';
import {
  FiHome,
  FiClock,
  FiCalendar,
  FiFile,
  FiStar,
  FiUser,
  FiShield,
  FiGitBranch,
  FiBell,
  FiUsers,
  FiLayers,
  FiList,
  FiBarChart2,
  FiLogOut,
  FiMenu,
  FiX,
  FiUserMinus,
  FiSliders,
} from 'react-icons/fi';
import './Layout.css';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isExEmployee } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread notification count (skip for ex-employees)
  useEffect(() => {
    if (isExEmployee) return;
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationService.getUnreadCount();
        setUnreadCount(response.data.unreadCount || 0);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isExEmployee]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const exEmployeeNavItems = [
    { path: '/ex/personal-details', icon: FiUser, label: 'Personal Details' },
    { path: '/ex/timesheets', icon: FiClock, label: 'Timesheets' },
    { path: '/ex/leaves', icon: FiCalendar, label: 'Leaves' },
    { path: '/ex/documents', icon: FiFile, label: 'Documents' },
    { path: '/ex/performance', icon: FiStar, label: 'Performance' },
  ];

  const navItems = isExEmployee ? exEmployeeNavItems : [
    { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
    { path: '/timesheet', icon: FiClock, label: 'Timesheets' },
    { path: '/leave', icon: FiCalendar, label: 'Leaves' },
    { path: '/documents', icon: FiFile, label: 'Documents' },
    { path: '/performance-review', icon: FiStar, label: 'Performance' },
    { path: '/personal-details', icon: FiUser, label: 'Personal Details' },
    { path: '/visa', icon: FiShield, label: 'Visa' },
    { path: '/hierarchy', icon: FiGitBranch, label: 'Hierarchy' },
    { path: '/notifications', icon: FiBell, label: 'Notifications' },
  ];

  const adminItems = [
    { path: '/admin/employees', icon: FiUsers, label: 'Employees' },
    { path: '/admin/departments', icon: FiLayers, label: 'Departments' },
    { path: '/admin/leave-types', icon: FiList, label: 'Leave Types' },
    { path: '/admin/leave-allocations', icon: FiSliders, label: 'Leave Allocation' },
    { path: '/admin/reports', icon: FiBarChart2, label: 'Reports' },
    { path: '/admin/ex-employees', icon: FiUserMinus, label: 'Ex-Employees' },
  ];

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h1 className="logo">HR Manager</h1>
          <button className="sidebar-close" onClick={closeSidebar}>
            <FiX size={24} />
          </button>
        </div>

        {isExEmployee && (
          <div className="read-only-banner">
            READ-ONLY ACCESS
          </div>
        )}

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
              {item.path === '/notifications' && unreadCount > 0 && (
                <span className="badge badge-danger">{unreadCount}</span>
              )}
            </Link>
          ))}
        </nav>

        {!isExEmployee && isAdmin && (
          <>
            <div className="sidebar-divider"></div>
            <div className="admin-section">
              <h3>Admin</h3>
              <nav className="sidebar-nav">
                {adminItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${
                      isActive(item.path) ? 'active' : ''
                    }`}
                    onClick={closeSidebar}
                  >
                    <item.icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </>
        )}
      </aside>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      {/* Main Content */}
      <div className="main-wrapper">
        {/* Header */}
        <header className="header">
          <div className="header-left">
            <button className="hamburger" onClick={toggleSidebar}>
              <FiMenu size={24} />
            </button>
            <h2 className="page-title">
              {navItems.concat(adminItems).find((item) =>
                isActive(item.path)
              )?.label || 'Dashboard'}
            </h2>
          </div>

          <div className="header-right">
            {!isExEmployee && (
              <div className="notification-icon">
                <Link to="/notifications" className="notification-link">
                  <FiBell size={20} />
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </Link>
              </div>
            )}

            <div className="user-menu">
              <div className="user-avatar">{user?.firstName?.[0]?.toUpperCase()}</div>
              <span className="user-name">{user?.firstName} {user?.lastName}</span>
              <button
                className="logout-btn"
                onClick={handleLogout}
                title="Logout"
              >
                <FiLogOut size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
